<script lang="ts">
	import Icon from '@components/Icon.svelte';
	import { createEventDispatcher, type ComponentType } from 'svelte';

	interface IInputButtonProps {
		onClick?: () => void;
		icon: ComponentType;
		disabled?: boolean;
		to?: string;
		ariaLabel?: string;
		ariaHasPopup?: boolean | 'dialog' | 'grid' | 'listbox' | 'menu' | 'tree' | null | undefined;
		ariaExpanded?: boolean;
		ariaControls?: string;
	}
	export let value: string;
	export let label: string;
	export let hint: string | undefined = undefined;
	export let id: string;
	export let button: IInputButtonProps | undefined = undefined;
	export let requiredMessage: string | undefined = undefined;
	export let disabled: boolean = false;
	export let required: boolean = false;
	export let invalidMessage: string | undefined = `Bitte geben Sie eine gültige ${label} an`;
	let className = '';
	export { className as class };

	let isFocused: boolean = false;
	let inputElement: HTMLInputElement;
	let allowPerKeystrokeValidation = false;

	const initialValidityState = {
		badInput: false,
		customError: false,
		patternMismatch: false,
		rangeOverflow: false,
		rangeUnderflow: false,
		stepMismatch: false,
		tooLong: false,
		tooShort: false,
		typeMismatch: false,
		valid: true,
		valueMissing: false
	};
	let validityState = initialValidityState;

	const dispatch = createEventDispatcher();

	const errorId = `${id}-error`;
	const requiredId = `${id}-required`;

	function onButtonClick() {
		if (button?.onClick) {
			button.onClick();
		}
	}

	function onInputClick() {
		dispatch('click');
	}

	function onKeydown(e: KeyboardEvent) {
		if (e.key === 'Enter') {
			dispatch('enter');
		}
	}

	function onFocus() {
		isFocused = true;
	}

	function onBlur() {
		dispatch('blur');
		isFocused = false;
		if (!allowPerKeystrokeValidation) allowPerKeystrokeValidation = true;
		checkValidity();
	}

	const checkValidity = () => {
		if (inputElement?.checkValidity()) {
			validityState = initialValidityState;
		}
	};

	const onChangeValidityCheck = (e: InputEvent) => {
		dispatch('input', {
			value: (e.target as HTMLInputElement).value
		});

		if (allowPerKeystrokeValidation) {
			checkValidity();
		}
	};

	const handleInvalid = (e: Event, preventDefaultErrorTooltips = true) => {
		if (preventDefaultErrorTooltips) {
			e.preventDefault();
		}
		allowPerKeystrokeValidation = true;

		if (e.target instanceof HTMLInputElement) {
			const {
				badInput,
				customError,
				patternMismatch,
				rangeOverflow,
				rangeUnderflow,
				stepMismatch,
				tooLong,
				tooShort,
				typeMismatch,
				valid,
				valueMissing
			} = e.target.validity;

			// validity is a class instance therefore can't be copied with spread operator
			validityState = {
				badInput,
				customError,
				patternMismatch,
				rangeOverflow,
				rangeUnderflow,
				stepMismatch,
				tooLong,
				tooShort,
				typeMismatch,
				valid,
				valueMissing
			};
		}
	};

	$: shouldShowInvalidMessage = () => !validityState.valid && !validityState.valueMissing;
	$: shouldShowRequiredMessage = () => validityState.valueMissing;
</script>

<div class="input-outer-wrapper {className}">
	<div
		class="input-wrapper"
		class:disabled
		class:has-error={shouldShowInvalidMessage() || shouldShowRequiredMessage()}
	>
		<label class={`label  ${value || isFocused ? 'float-top' : ''}`} for={id}>
			{label}{required ? '*' : ''}
		</label>
		<input
			{id}
			on:focus={onFocus}
			on:blur={onBlur}
			on:input={onChangeValidityCheck}
			on:invalid={handleInvalid}
			on:click={onInputClick}
			on:keydown={(e) => onKeydown(e)}
			class="input-element peer"
			type="text"
			bind:value
			bind:this={inputElement}
			{disabled}
			{required}
			aria-describedby={shouldShowInvalidMessage()
				? errorId
				: shouldShowRequiredMessage()
					? requiredId
					: undefined}
			aria-invalid={shouldShowInvalidMessage() || shouldShowRequiredMessage()}
			{...$$restProps}
		/>
		{#if button}
			<button
				class="input-button"
				disabled={button.disabled}
				on:click={onButtonClick}
				{...button.ariaLabel ? { 'aria-label': button.ariaLabel } : {}}
				{...button.ariaHasPopup ? { 'aria-haspopup': button.ariaHasPopup } : {}}
				{...button.ariaExpanded ? { 'aria-expanded': button.ariaExpanded } : {}}
				{...button.ariaControls ? { 'aria-controls': button.ariaControls } : {}}
			>
				{#if button.icon}
					<Icon component={button.icon} />
				{/if}</button
			>
		{/if}
	</div>
	{#if hint}<p class="mt-1.5 text-additional font-bold">{hint}</p>{/if}
	{#if shouldShowRequiredMessage()}
		<p
			id={requiredId}
			class="mt-1.5 text-function-red text-additional font-bold"
			role="alert"
			aria-live="polite"
		>
			{requiredMessage}
		</p>
	{/if}
	{#if invalidMessage && shouldShowInvalidMessage()}
		<p
			id={errorId}
			class="mt-1.5 text-function-red text-additional font-bold"
			role="alert"
			aria-live="polite"
		>
			{invalidMessage}
		</p>
	{/if}
</div>

<style lang="postcss">
	.input-wrapper {
		@apply box-border
		flex-grow
		h-13.5
		w-full
		align-top
		inline-block
		transition-all
		ease-in-out
		duration-200
		relative
		bg-white
		dark:bg-gray-2
		dark:focus-within:bg-gray-3
		dark:hover:bg-gray-3
		border
		border-gray-4
		dark:border-gray-7
		focus-within:bg-gray-11
		hover:bg-gray-11
		rounded;
	}

	.input-wrapper.has-error {
		@apply border-function-red bg-function-red-bg;
	}

	.input-wrapper.disabled {
		@apply pointer-events-none 	opacity-50;
	}

	.input-wrapper.has-error .label {
		@apply text-function-red;
	}

	.input-wrapper.has-error .input-element {
		@apply text-function-red focus:text-gray-1;
	}

	.label {
		@apply absolute
      pointer-events-none
      transition-all
      ease-in-out
      duration-200
      h-full
      bg-transparent
      leading-[3.375rem]
      pl-3
      text-gray-4
      dark:text-gray-9
      text-lg;
	}

	.label.float-top {
		@apply text-[0.9375rem] leading-8;
	}

	.input-element {
		@apply font-bold
    focus:outline-none
    outline-none
    focus:ring-0
    text-lg
    text-gray-1
    dark:text-white
    w-full
    h-full
    px-3
	pt-6.75
    pb-1.5
    bg-transparent
    rounded;
	}

	.input-button {
		@apply absolute p-2 right-1 top-1/2 -translate-y-1/2;
	}

	.input-button svg {
		@apply w-[1.125rem] h-[1.125rem];
	}

	.error {
		@apply text-function-red;
	}
</style>
